import React, { useState } from 'react'
import { Modal } from '../Modal/Modal'
import { ConsentIcon, SimpleConsentIcon } from './ConsentIcon'
import { ExternalUserConsentChange, User } from '../../models/User'
import { Col, Container, Row } from 'react-bootstrap'
import './ConsentModal.scss'
import { Button } from '../Button/Button'
import { UserConsent, UserConsentState } from './model'
import { useGetRoomSessionQuery } from '../../store/media/media'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { parseMetadata } from '../../apps/professional-app/fa/MeetingSummary/utils'
import { RoomSessionListItem } from '../../apps/professional-app/fa/PastMeetings/PastMeetings'
import { TableColumn } from 'react-data-table-component'
import { formatDate } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { NavLink } from 'react-router-dom'
import buttonStyles from '../Button/Button.module.scss'
import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { useGetExternalUserConsentHistoryQuery } from '../../store/externalUser/externalUser'
import { ZocksDataTable } from '../DataTable/ZocksDataTable'

type ConsentModalProps = {
  show: boolean
  onCancel: () => void
  contact: User
  consent?: UserConsent | null
}

export const ConsentModal: React.FC<ConsentModalProps> = ({ consent, show, onCancel, contact }) => {
  const accountId = useAppSelector(selectAccountId)
  const metadata = parseMetadata(contact.metadata ?? '')
  const userConsent = consent ?? (typeof metadata === 'object' && metadata !== null && metadata.zocksConsent)
  const { room_id: roomId, session_id: sessionId } = userConsent?.current || {}
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)

  const { data: roomSession } = useGetRoomSessionQuery({ accountId, roomId, sessionId }, { skip: !roomId, refetchOnMountOrArgChange: true })

  const { data: consentChanges, isFetching: isConsentChangesFetching } = useGetExternalUserConsentHistoryQuery(
    {
      accountId,
      userId: contact.id,
      pageRequest: { page: page - 1, size: perPage },
    },
    { refetchOnMountOrArgChange: true },
  )
  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  const renderConversationLine = (conversationLine: string) => {
    const [name, ...msgParts] = conversationLine.split(': ')
    const msg = msgParts.join(': ')

    return (
      <>
        <strong>{name}: </strong>
        {msg}
      </>
    )
  }

  const columns: TableColumn<ExternalUserConsentChange>[] = [
    {
      name: 'Consent',
      selector: (row) => row.newValue || UserConsentState.UNKNOWN,
      sortable: true,
      grow: 1,
      cell: (row) => <SimpleConsentIcon consentState={row.newValue || UserConsentState.UNKNOWN} showText={true} />,
    },
    {
      name: 'Date',
      selector: (row) => formatDate(row.changedAt),
      sortable: true,
      grow: 2,
      width: '25%',
    },
    {
      name: 'Meeting ID',
      selector: (row) => row.sessionId || '<none>',
      sortable: true,
      cell: (row) => (
        <>
          {row.roomId && row.sessionId ? (
            <NavLink
              className={`${buttonStyles.button} ${buttonStyles.linkButton} button-link-medium `}
              to={`/workspace/past-meetings/room/${row.roomId}/session/${row.sessionId}`}
            >
              {row.sessionId}
            </NavLink>
          ) : (
            <span>&lt;none&gt;</span>
          )}
        </>
      ),
    },
  ]

  const ModalBody = (
    <Container>
      <Row>
        <Col className='text-center'>
          <h3>Consent tracking status</h3>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>User consent status</Col>
      </Row>
      <Row className='mt-2 mb-6'>
        <Col>
          <ConsentIcon consent={consent} contact={contact}></ConsentIcon>
        </Col>
      </Row>

      {(!userConsent || userConsent.current?.state === 'unknown') && (
        <Row className='mt-6'>
          <Col>
            <div className='p-4 zocks-border-0.25 zocks-bg-color-gray-100'>There was no relevant information during the meeting.</div>
          </Col>
        </Row>
      )}

      {userConsent?.current?.context && userConsent?.current?.context.length > 0 && (
        <Row className='mt-6'>
          <Col>
            <div className='mb-2'>Conversation</div>
            <div className='p-4 zocks-border-0.25 zocks-bg-color-gray-100 overflow-auto zocks-mh-250'>
              {Array.isArray(userConsent.current.context) ? (
                <ul className='list-inline'>
                  {userConsent.current.context.map((c: string, index: number) => (
                    <li key={`consent-line-${index}`}>{renderConversationLine(c)}</li>
                  ))}
                </ul>
              ) : (
                <ul className='list-inline'>
                  {userConsent.current.context.split('\n').map((c: string, index: number) => (
                    <li key={`consent-line-${index}`}>{renderConversationLine(c)}</li>
                  ))}
                </ul>
              )}
            </div>
          </Col>
        </Row>
      )}

      {roomSession && roomSession.data && (userConsent?.current.discussed || userConsent?.current.permissionAsked) && (
        <Row className='mt-4'>
          <Col>
            <RoomSessionListItem session={roomSession.data} key={roomSession.data.id} onClick={onCancel} hideDeleteButton={true} />
          </Col>
        </Row>
      )}
      <Row className='pt-4'>
        <Col>
          <h4>History</h4>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={consentChanges?.data || []}
            progressPending={isConsentChangesFetching}
            pagination
            paginationServer
            paginationTotalRows={consentChanges?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )

  const ModalFooter = (
    <div className='d-flex gap-3'>
      <Button variant='primary' onClick={onCancel}>
        Close
      </Button>
    </div>
  )

  return <Modal className='consent-modal' show={show} onHide={onCancel} header={<></>} body={ModalBody} footer={ModalFooter} centered={true} />
}
